import { computed } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { validateStandardSchema } from '@angular/forms/signals';

import type {
    ImageEntity,
    ImageStylesLegacy,
    LegacyImageDefault,
    LegacyImageZero,
    SelectedFileLegacy,
} from '@sites/entity-core';
import {
    clampToBounds,
    IMAGE_BORDER_RADIUS_BOUNDS,
    imageLegacyDefaultV0Schema,
    imageLegacyZeroV0Schema,
} from '@sites/entity-core';

import type { BaseLegacyEntityForm, BaseLegacyEntityFormOptions } from '../../core/create-base-legacy-entity-form';
import { configureCommonReadonly, createBaseLegacyEntityForm } from '../../core/create-base-legacy-entity-form';
import { composeForm } from '../../core/compose-form';
import { animationPlugin } from '../../plugins/widget/animation.plugin';
import { extendedPositionPlugin, widgetPositionPlugin } from '../../plugins/widget/position.plugin';
import { visibilityPlugin } from '../../plugins/widget/visibility.plugin';
import { shadowPlugin } from '../../plugins/widget/shadow.plugin';
import { legacyWidgetEnvelopePlugin } from '../../plugins/widget/legacy-widget-envelope.plugin';

export type ImageFormModel = ImageEntity;
export type ImageDataModel = ImageEntity['data'];

/**
 * Image-specific accessors shared verbatim by both variant assemblers. These
 * are structurally identical on both variants (only `position` differs, and
 * that comes from the position plugins; the zero-only `imagePosition` is added
 * by the zero assembler), so the lazy `raw` thunk is narrowed to the default
 * variant's FieldTree internally.
 *
 * Note these reach into **two** envelope branches: widget `data` (image, align,
 * resize, …) and the structured `styles` group (border / radius / max-width) —
 * the image widget is the only one whose `styles` is a typed bag rather than
 * the open JSS record. Exposed via getters so the FieldTree paths materialise
 * lazily (after `syncFromEntity` populates the model) — composed with
 * {@link composeForm}, never object spread.
 */
function imageSharedAccessors(raw: () => FieldTree<LegacyImageDefault> | FieldTree<LegacyImageZero>) {
    const r = raw as () => FieldTree<LegacyImageDefault>;
    return {
        // --- Content ---
        get image() {
            return r().data.image;
        },
        setImage: (value: SelectedFileLegacy | null) => {
            r().data.image().value.set(value);
        },
        /** Immutable patch over the current picked file (no-op when empty). */
        updateImage: (patch: Partial<SelectedFileLegacy>) => {
            r()
                .data.image()
                .value.update((current) => (current ? { ...current, ...patch } : current));
        },

        // --- Alignment ---
        get horizontalAlign() {
            return r().data.horizontalAlign;
        },
        setHorizontalAlign: (value: ImageDataModel['horizontalAlign']) => {
            r().data.horizontalAlign().value.set(value);
        },
        get mobileHorizontalAlign() {
            return r().data.mobileHorizontalAlign;
        },
        setMobileHorizontalAlign: (value: ImageDataModel['mobileHorizontalAlign']) => {
            r().data.mobileHorizontalAlign().value.set(value);
        },
        get tabletHorizontalAlign() {
            return r().data.tabletHorizontalAlign;
        },
        setTabletHorizontalAlign: (value: ImageDataModel['tabletHorizontalAlign']) => {
            r().data.tabletHorizontalAlign().value.set(value);
        },

        // --- Layout / resize ---
        get resizeType() {
            return r().data.resizeType;
        },
        setResizeType: (value: ImageDataModel['resizeType']) => {
            r().data.resizeType().value.set(value);
        },
        get isSynchronizeStyle() {
            return r().data.isSynchronizeStyle;
        },
        /**
         * Style-synchronisation flag with the legacy-null rule applied: very
         * old widgets persisted `null`, which the FormGroup editor treated as
         * "on" — truthy check on purpose.
         */
        isSynchronized: computed<boolean>(() => Boolean(r().data.isSynchronizeStyle?.().value())),
        setIsSynchronizeStyle: (value: boolean) => {
            r().data.isSynchronizeStyle().value.set(value);
        },

        // --- Border (toggle + independent corners) ---
        get isBorder() {
            return r().data.isBorder;
        },
        setIsBorder: (value: boolean) => {
            r().data.isBorder().value.set(value);
        },
        get isIndependentCorners() {
            return r().data.isIndependentCorners;
        },
        setIsIndependentCorners: (value: boolean) => {
            r().data.isIndependentCorners().value.set(value);
        },

        // --- Structured styles group (envelope-level `styles`, not `data`) ---
        get borderRadius() {
            return r().styles.borderRadius;
        },
        setBorderRadius: (value: ImageStylesLegacy['borderRadius']) => {
            r().styles.borderRadius().value.set(clampToBounds(value, IMAGE_BORDER_RADIUS_BOUNDS));
        },
        get borderTopLeftRadius() {
            return r().styles.borderTopLeftRadius;
        },
        setBorderTopLeftRadius: (value: ImageStylesLegacy['borderTopLeftRadius']) => {
            r().styles.borderTopLeftRadius().value.set(clampToBounds(value, IMAGE_BORDER_RADIUS_BOUNDS));
        },
        get borderTopRightRadius() {
            return r().styles.borderTopRightRadius;
        },
        setBorderTopRightRadius: (value: ImageStylesLegacy['borderTopRightRadius']) => {
            r().styles.borderTopRightRadius().value.set(clampToBounds(value, IMAGE_BORDER_RADIUS_BOUNDS));
        },
        get borderBottomRightRadius() {
            return r().styles.borderBottomRightRadius;
        },
        setBorderBottomRightRadius: (value: ImageStylesLegacy['borderBottomRightRadius']) => {
            r().styles.borderBottomRightRadius().value.set(clampToBounds(value, IMAGE_BORDER_RADIUS_BOUNDS));
        },
        get borderBottomLeftRadius() {
            return r().styles.borderBottomLeftRadius;
        },
        setBorderBottomLeftRadius: (value: ImageStylesLegacy['borderBottomLeftRadius']) => {
            r().styles.borderBottomLeftRadius().value.set(clampToBounds(value, IMAGE_BORDER_RADIUS_BOUNDS));
        },
        get maxWidth() {
            return r().styles.maxWidth;
        },
        setMaxWidth: (value: ImageStylesLegacy['maxWidth']) => {
            r().styles.maxWidth().value.set(value);
        },
        get borderWidth() {
            return r().styles.borderWidth;
        },
        setBorderWidth: (value: ImageStylesLegacy['borderWidth']) => {
            r().styles.borderWidth().value.set(value);
        },
        get borderStyle() {
            return r().styles.borderStyle;
        },
        setBorderStyle: (value: ImageStylesLegacy['borderStyle']) => {
            r().styles.borderStyle().value.set(value);
        },
        get borderColor() {
            return r().styles.borderColor;
        },
        setBorderColor: (value: ImageStylesLegacy['borderColor']) => {
            r().styles.borderColor().value.set(value);
        },
        get advancedBorderSettings() {
            return r().styles.advancedBorderSettings;
        },
        setAdvancedBorderSettings: (value: ImageStylesLegacy['advancedBorderSettings']) => {
            r().styles.advancedBorderSettings().value.set(value);
        },
    };
}

/**
 * Per-variant accessor assemblers. The two image variants share every shared
 * accessor and the structured styles group; they differ on the `position`
 * branch type (simple `WidgetPosition` for default, full `ExtendedPosition`
 * for zero) and on the zero-only `imagePosition` focal-point keyword.
 */
function assembleDefaultImageForm(base: BaseLegacyEntityForm<LegacyImageDefault>) {
    const _raw = base._raw;

    // Checked widening (no cast): the variant form is assignable to the
    // entity-union form, so the dispatcher's union return collapses into a
    // single useful shape for union-only callers.
    const widenedBase: BaseLegacyEntityForm<ImageEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        imageSharedAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        animationPlugin(() => _raw.data),
        shadowPlugin(() => _raw.data),
        // Default-only layout — the simple per-device position grid.
        widgetPositionPlugin(() => _raw.data)
    );
}

function assembleZeroImageForm(base: BaseLegacyEntityForm<LegacyImageZero>) {
    const _raw = base._raw;

    const widenedBase: BaseLegacyEntityForm<ImageEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        imageSharedAccessors(() => _raw),
        // Zero-only focal-point keyword (`object-position` in the view).
        {
            get imagePosition() {
                return _raw.data.imagePosition;
            },
            setImagePosition: (value: LegacyImageZero['data']['imagePosition']) => {
                _raw.data.imagePosition().value.set(value);
            },
        },
        visibilityPlugin(() => _raw.data),
        animationPlugin(() => _raw.data),
        shadowPlugin(() => _raw.data),
        // Zero-only layout — the full extended-position grid.
        extendedPositionPlugin(() => _raw.data)
    );
}

/**
 * Signal-form view of a **default-placement** image widget. Validates against
 * the legacy default envelope schema, which permits an absent `type` field for
 * parity with pre-discriminator persisted data on disk. The `'specific'`
 * editor placement shares this form (same persisted shape).
 *
 * **Field accessors are FieldTree references** — exposed via getters so the
 * FieldTree paths materialise lazily (after `syncFromEntity` populates the
 * model). Consumers call them to get the `FieldState`, then read `.value()`,
 * `.touched()`, `.dirty()`, `.errors()` as needed. Convenience `setX(v)`
 * methods are sugar over `field().value.set(v)`.
 */
export function createLegacyImageDefaultForm(options?: BaseLegacyEntityFormOptions<LegacyImageDefault>) {
    const base = createBaseLegacyEntityForm<LegacyImageDefault>((schemaPath) => {
        validateStandardSchema(schemaPath, imageLegacyDefaultV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    return assembleDefaultImageForm(base);
}

/**
 * Signal-form view of a **zero-block-placement** image widget. Validates
 * against the legacy zero envelope schema, which requires `type: 'zero'` on
 * the envelope (the host-app factory copies `meta.type` onto the entity at
 * creation time).
 *
 * Diverges from {@link createLegacyImageDefaultForm} on `position` (the full
 * `ExtendedPosition` grid) and adds the zero-only `imagePosition` accessor.
 */
export function createLegacyImageZeroForm(options?: BaseLegacyEntityFormOptions<LegacyImageZero>) {
    const base = createBaseLegacyEntityForm<LegacyImageZero>((schemaPath) => {
        validateStandardSchema(schemaPath, imageLegacyZeroV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    return assembleZeroImageForm(base);
}

/**
 * Variant dispatcher. Callers that only know `ImageEntity` (the union of
 * default + zero) use this — `WebLegacyWidgetSignalFormService` for instance — and
 * the dispatcher reads the variant tag from `options.initialData?.type` to pick
 * the matching per-variant factory.
 *
 * The return type is the **union** of the two variant forms. Consumers that
 * want to read variant-specific accessors (the `position` branch types diverge,
 * and `imagePosition` is zero-only) must narrow via
 * `form.value().type === 'zero'` first, or use the variant factories directly.
 */
export function createImageForm(options?: BaseLegacyEntityFormOptions<ImageEntity>) {
    const variant = options?.initialData?.type;

    if (variant === 'zero') {
        return createLegacyImageZeroForm(options as BaseLegacyEntityFormOptions<LegacyImageZero>);
    }

    return createLegacyImageDefaultForm(options as BaseLegacyEntityFormOptions<LegacyImageDefault>);
}

export type ImageForm = ReturnType<typeof createImageForm>;
export type ImageDefaultForm = ReturnType<typeof createLegacyImageDefaultForm>;
export type ImageZeroForm = ReturnType<typeof createLegacyImageZeroForm>;
